/**
 * Admin foods table component.
 *
 * Displays base foods with macros, tags, and food groups.
 * Data provided by connected wrapper via props.
 */

import * as stylex from "@stylexjs/stylex";
import { useState } from "react";

import { alert, layout, spacing, table, text } from "@animus/primaris";

import type { NutritionFood } from "./types.ts";
import { FoodDetailModal } from "./food-detail-modal.tsx";
import { FoodRow } from "./food-row.tsx";
import { ImportFoodsButton } from "./import-foods-button.tsx";

interface AdminFoodsProps {
  foods: NutritionFood[];
  isLoading?: boolean;
  error?: string | null;
}

function AdminFoods({ foods, isLoading, error }: AdminFoodsProps) {
  const [selectedFood, setSelectedFood] = useState<NutritionFood | null>(null);

  return (
    <div>
      <div {...stylex.props(spacing.mb6, layout.flexBetween)}>
        <div>
          <h1 {...stylex.props(text.bold, text["2xl"], text.white)}>
            Base Foods
          </h1>
          <p {...stylex.props(text.sm, text.muted)}>
            Canonical food database with per-100g nutrition data
          </p>
        </div>
        <div {...stylex.props(layout.flexRow, layout.gap2)}>
          <ImportFoodsButton />
        </div>
      </div>

      {error && <div {...stylex.props(alert.error, spacing.mb4)}>{error}</div>}

      {isLoading ? (
        <p {...stylex.props(text.muted)}>Loading foods...</p>
      ) : (
        <div {...stylex.props(table.container)}>
          <table {...stylex.props(table.table)}>
            <thead {...stylex.props(table.thead)}>
              <tr>
                <th {...stylex.props(table.th)}>Food</th>
                <th {...stylex.props(table.th)}>Cal</th>
                <th {...stylex.props(table.th)}>P</th>
                <th {...stylex.props(table.th)}>C</th>
                <th {...stylex.props(table.th)}>F</th>
                <th {...stylex.props(table.th)}>Group</th>
                <th {...stylex.props(table.th)}>Tags</th>
                <th {...stylex.props(table.th)}>Updated</th>
                <th {...stylex.props(table.th, table.thRight)}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {foods.map((food) => (
                <FoodRow
                  food={food}
                  key={food.id}
                  onViewDetails={() => setSelectedFood(food)}
                />
              ))}
              {foods.length === 0 && (
                <tr>
                  <td {...stylex.props(table.empty)} colSpan={9}>
                    No foods found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {selectedFood && (
        <FoodDetailModal
          food={selectedFood}
          onClose={() => setSelectedFood(null)}
        />
      )}
    </div>
  );
}

export { AdminFoods, type AdminFoodsProps };
